import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import IntlTelInput from 'react-intl-tel-input'
import { LabelElement, inputStyle, inputBorderStyle } from 'components/Input'
import { numberise } from 'tools/style'
import { isBrowser, coreResource } from 'env'
import TelephoneInputGlobalStyle from './TelephoneInput.style.js'
import { colors, colorConstants, padding, paddingSizes } from 'theme/default.js'
import { isEmpty } from 'tools/string'
import { init } from 'tools/lib'

const flagContainerSize = 55
const inputPadding = flagContainerSize + numberise(padding[paddingSizes.small])

const InputWrapper = styled.div`
  position: relative;

  input {
    ${inputStyle}
    ${inputBorderStyle}
  }

  .intl-tel-input {
    width: 100%;
  }

  .intl-tel-input.allow-dropdown.separate-dial-code .selected-dial-code {
    display: none;
  }

  .intl-tel-input.allow-dropdown.separate-dial-code.iti-sdc-2,
  .intl-tel-input.allow-dropdown.separate-dial-code.iti-sdc-3,
  .intl-tel-input.allow-dropdown.separate-dial-code.iti-sdc-4,
  .intl-tel-input.allow-dropdown.separate-dial-code.iti-sdc-5 {
    .selected-flag {
      width: ${flagContainerSize}px;
    }
    input[type='tel'],
    input[type='text'] {
      padding-left: ${inputPadding}px;
      background-color: ${(props) =>
        props.disabled ? colorConstants.lightestGray : colorConstants.white};
      color: ${colors.text};
    }
  }

  ${(props) =>
    props.disabled || !props.allowDropdown
      ? `
          pointer-events: none;
          .intl-tel-input .flag-container .selected-flag .arrow {
            display: none;
          }
        `
      : ''}
`

const TelephoneInputLabel = styled(LabelElement).attrs(() => ({
  left: `${inputPadding}px`
}))``

const defaultUtilsScriptPath = coreResource('/js/libphonenumber.js')
const defaultFlagImagePath = {
  mobile: coreResource('/images/flags.png'),
  desktop: coreResource('/images/flags@2x.png')
}

const formatPhoneNumber = (number, country, format) => {
  if (isBrowser() && window.intlTelInputUtils) {
    const formattedPhoneNumber = window.intlTelInputUtils.formatNumber(
      number,
      country && country.iso2,
      window.intlTelInputUtils.numberFormat[format]
    )
    return formattedPhoneNumber
  }

  return number
}

const TelephoneInput = ({
  name,
  label,
  className = '',
  defaultCountryCode = 'sg',
  utilsScript = defaultUtilsScriptPath,
  flagImagePath = defaultFlagImagePath,
  tabIndex,
  required,
  disabled,
  withError,
  value: valueFromProps,
  onChange = () => {},
  format = true,
  controlled = false,
  onFocus = () => {},
  onBlur = () => {},
  allowDropdown = true,
  ...props
}) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const [selectedCountry, setSelectedCountry] = React.useState(null)
  const [nationalNumber, setNationalNumber] = React.useState(valueFromProps)
  const intlTelInputRef = React.useRef()
  const isUtilsScriptLoadedRef = React.useRef(false)

  // Update internal state of IntlTelInput
  const setPhoneNumber = (phoneNumber, counter) => {
    if (intlTelInputRef.current) {
      intlTelInputRef.current.updateFlagFromNumber(phoneNumber || '', true)
      intlTelInputRef.current.updateValFromNumber(phoneNumber || '', true, true)
    }
  }

  // WORKAROUND: Load the util script ourselves instead of letting IntlTelInput to load the script.
  // Reason: IntlTelInput reset its internal state which will reset the country to the defaultCountryCode props when the script finish loading.
  React.useEffect(() => {
    if (utilsScript && !isUtilsScriptLoadedRef.current) {
      init(utilsScript, 'intlTelInputUtilsScript').then(() => {
        isUtilsScriptLoadedRef.current = true
        if (controlled && valueFromProps) {
          // After util scripts is loaded, format phone number
          setPhoneNumber(valueFromProps)
        }
      })
    }
  }, [controlled, utilsScript, valueFromProps])

  React.useEffect(() => {
    // If in controlled mode, format value in state and value from props,
    // then compare them and sync to local state if they are different
    if (controlled) {
      const formattedValueFromState = formatPhoneNumber(
        nationalNumber,
        selectedCountry,
        'INTERNATIONAL'
      ) // e.g. +65 9090 9090
      const formattedValueFromProps = formatPhoneNumber(
        valueFromProps,
        selectedCountry,
        'INTERNATIONAL'
      ) // e.g. +65 9090 9090

      if (formattedValueFromState !== formattedValueFromProps) {
        setPhoneNumber(formattedValueFromProps)
        setNationalNumber(formattedValueFromProps)
      }
    }
  }, [controlled, valueFromProps, selectedCountry, nationalNumber])

  const onPhoneNumberChange = (valid, value, country) => {
    // Update own state
    setSelectedCountry(country)
    setNationalNumber(value)

    // Execute onChange callback
    const formattedPhoneNumber = formatPhoneNumber(value, country, 'E164') // e.g. +6590909090
    if (!disabled) onChange(name, formattedPhoneNumber)
  }

  const onSelectFlag = (inputValue, selectedCountry) => {
    onPhoneNumberChange(false, inputValue, selectedCountry)
  }

  const onInputFocus = () => {
    setIsFocused(true)
    onFocus()
  }

  const onInputBlur = () => {
    setIsFocused(false)
    onBlur()
  }
  // WORKAROUND: separateDialCode need to be enabled to make copy-pasted pre-formatted phone number with dial code works (e.g. +660846125110)
  return (
    <React.Fragment>
      <InputWrapper
        disabled={disabled}
        withError={withError}
        focus={isFocused}
        allowDropdown={allowDropdown}
      >
        {/* Reference: https://github.com/patw0929/react-intl-tel-input/blob/master/src/components/IntlTelInput.js#L1306-L1326 */}
        <IntlTelInput
          {...props}
          allowDropdown={allowDropdown}
          disabled={disabled}
          ref={intlTelInputRef}
          defaultCountry={defaultCountryCode}
          preferredCountries={[defaultCountryCode]}
          css={['intl-tel-input', className]}
          fieldName={name}
          fieldId={name}
          placeholder=''
          telInputProps={{
            tabIndex,
            required,
            pattern: '^[+]?[0-9 -]+'
          }}
          value={nationalNumber || ''}
          format={format}
          separateDialCode
          onSelectFlag={onSelectFlag}
          onPhoneNumberBlur={onInputBlur}
          onPhoneNumberFocus={onInputFocus}
          onPhoneNumberChange={onPhoneNumberChange}
        />
        <TelephoneInputLabel
          htmlFor={name}
          focus={isFocused || !isEmpty(nationalNumber)}
        >
          {label}
        </TelephoneInputLabel>
      </InputWrapper>
      <TelephoneInputGlobalStyle flagImagePath={flagImagePath} />
    </React.Fragment>
  )
}

TelephoneInput.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  tabIndex: PropTypes.number,
  defaultCountryCode: PropTypes.string,
  format: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  withError: PropTypes.bool,
  value: PropTypes.string,
  utilsScript: PropTypes.string,
  flagImagePath: PropTypes.shape({
    mobile: PropTypes.string.isRequired,
    desktop: PropTypes.string.isRequired
  }),
  allowDropdown: PropTypes.bool,
  controlled: PropTypes.bool
}

export default TelephoneInput
